/* eslint-disable max-lines */
import React, { ComponentProps, FC, useEffect, useMemo, useState } from 'react'

import { styled } from '@linaria/react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

import { addInterestInSearchOptionsAction } from 'actions/mainSearchAction'
import { VoidHandler } from 'common/types'
import { About } from 'components/block/About/About'
import { ControlButtonsBar } from 'components/block/ControlButtonsBar/ControlButtonsBar'
import { NameVariants } from 'components/block/NameVariants/NameVariants'
import { ShareComplaintProfile } from 'components/block/ShareComplaintMenu/ShareComplaintMenu'
import { ListCountriesFlag } from 'components/designSystem/ListCountriesFlag/ListCountriesFlag'
import { useScrollbar } from 'components/designSystem/Scrollbar/hooks/useScrollbar'
import { Scrollbar } from 'components/designSystem/Scrollbar/Scrollbar'
import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { Tags } from 'components/designSystem/Tags/Tags'
import {
  appearanceBlock,
  appearanceBlockDataName,
  educationBlock,
  lifestyleBlock,
  lifeStyleBlockDataName,
} from 'components/page/DatingProfile/DatingProfile.constants'
import { GQLForeignRootQuery } from 'components/page/ForeignProfile/gql/__generated__/ForeignRoot'
import { GQLPhotoItemFragment } from 'components/page/ForeignProfile/gql/__generated__/Photos'
import { switchBodyOverflow } from 'components/page/PhotoRating/PhotoRatingDraggable.functions'
import { Direction } from 'components/page/RatingWave/Rating.types'
import { useLgbtDetail } from 'hooks/useLgbtDetail'
import { useModalOpened } from 'hooks/useModalOpened'

import { ProfileNextContext } from './context/ProfileNextContext'
import { findAstroLexeme } from './function/findAstroLexeme'
import { findDatingGoals } from './function/findDatingGoals'
import { findLexeme } from './function/findLexeme'
import { useMobilePhotosNext } from './hooks/useMobilePhotosNext'
import { usePhotosNext } from './hooks/usePhotosNext'
import { useProfileFillingFields } from './hooks/useProfileFillingFields'
import { ClipPathInner, ClipPathOuter } from './profileMain.styled'
import { ProfileCard } from '../ProfileCard/ProfileCard'
import { ProfileFooter } from '../ProfileFooter/ProfileFooter'
import { ProfileList } from '../ProfileList/ProfileList'
import { ProfileLocation } from '../ProfileLocation/ProfileLocation'
import { ProfileMoreSwitch } from '../ProfilePhoto/ProfileMoreSwitch'
import { ProfilePhoto } from '../ProfilePhoto/ProfilePhoto'
import { ProfilePhotos } from '../ProfilePhotos/ProfilePhotos'
import { ProfileTarget } from '../ProfileTarget/ProfileTarget'
import { ProfileTemplate } from '../ProfileTemplate/ProfileTemplate'
import { ProfileText } from '../ProfileText/ProfileText'

export const ProfileMain: FC<{
  profile: GQLForeignRootQuery['user']
  photos?: GQLPhotoItemFragment[]
  enable: boolean
  ui: GQLForeignRootQuery['ui'] | null
  place: 'profile' | 'rating'
  onHandleClickControlButton: (action: Direction) => void
  onCallbackComplaint?: VoidHandler
}> = ({
  profile,
  photos = [],
  enable,
  ui,
  place,
  onHandleClickControlButton,
  onCallbackComplaint,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const hasModal = useModalOpened()

  const [scrollableNode, setScrollableNode] = useState<HTMLElement | null>(null)
  const [innerNode, setInnerNode] = useState<HTMLElement | null>(null)

  const { scrollbarRef } = useScrollbar(scrollableNode, innerNode)

  const { hasPhotoForSocial, hasPhotoForTag } = useProfileFillingFields(profile)
  const { mainPhotos, restPhotos } = usePhotosNext(photos)
  const {
    mobileGoalPhoto,
    mobileInterestsPhoto,
    mobileSocialPhoto,
    mobilePhotos,
  } = useMobilePhotosNext(photos, hasPhotoForSocial, hasPhotoForTag)

  const { lgbtRestriction, lgbtHidden, lgbtModified } = useLgbtDetail(
    profile?.gender
  )

  const hadVoted = useMemo(() => {
    if (photos.length > 0) {
      return photos[0].liked
    }
  }, [photos])

  const contextValue = useMemo<ProfileNextContext>(
    () => ({
      place,
      profileId: profile?.id,
      incognito: profile?.incognito,
      voteable: photos.length > 0 ? photos[0].voteable : false,
      hadVoted,
      enable,
    }),
    [enable, hadVoted, photos, place, profile]
  )

  useEffect(() => {
    switchBodyOverflow(true)

    return () => {
      switchBodyOverflow(false)
    }
  }, [hasModal])

  if (profile && ui) {
    const { from: targetStartAge, to: targetFinishAge } =
      profile.dating.lookForAge

    const targetList = findDatingGoals(profile, ui, lgbtRestriction)

    const languages = profile.dating.knownLanguages?.map((language) => {
      return ui.glossary.datingProfile.knownLanguages.find(
        (_language) => _language.value === language
      )?.lexeme!
    })

    const lifestyle = findLexeme(
      profile,
      ui,
      lifestyleBlock,
      lifeStyleBlockDataName,
      intl,
      lgbtRestriction,
      lgbtHidden,
      lgbtModified
    )

    const appearance = findLexeme(
      profile,
      ui,
      appearanceBlock,
      appearanceBlockDataName,
      intl
    )

    const education = findLexeme(profile, ui, educationBlock, {}, intl)

    const astro = findAstroLexeme(profile, intl)

    const mobileLastPhotos = mobilePhotos && mobilePhotos.length > 0 && (
      <ProfileMoreSwitch photos={mobilePhotos} />
    )

    const handleSaveInterestSearchClick = (id: number) => {
      dispatch(addInterestInSearchOptionsAction(id))
    }

    const nameProps: ComponentProps<typeof NameVariants> = {
      name: profile.name,
      age: profile.age,
      isVerified: profile.verification.verifiedPhotos,
      isOnline: profile.online?.status,
      fontSize: 32,
      fontWeight: 700,
      gap: 8,
      variant: 'footer-status',
    }

    const handleScroll = () => {
      scrollableNode?.style.setProperty(
        '--dynamic-scroll-y',
        `${scrollableNode?.scrollTop}px`
      )
    }

    return (
      <ProfileNextContext.Provider value={contextValue}>
        <Parent data-name={`profile-${profile.id}`}>
          <ClipPathInner>
            <ProfileParent ref={setScrollableNode} onScroll={handleScroll}>
              <ProfilePhotos
                userId={profile.id}
                gender={profile.gender}
                photos={mainPhotos}
                incognito={profile.incognito}
                restPhotos={restPhotos}
                personalBlock={
                  <Name
                    {...nameProps}
                    singleLine={true}
                    variant="inline-status"
                  />
                }
                isOnline={profile.online?.status}
                hasVerified={profile.verification?.verifiedPhotos}
              />
              <ProfileCard
                data-name={`personal-info-${profile.id}`}
                personalBlock={<Name {...nameProps} />}
                ref={setInnerNode}
                targetBlock={
                  <ProfileTarget
                    targetGender={profile.dating.lookFor}
                    userGender={profile.gender}
                    targetStartAge={targetStartAge}
                    targetFinishAge={targetFinishAge}
                    lexemes={ui.glossary.datingProfile.lookFor}
                    lgbtHidden={lgbtHidden}
                    lgbtModified={lgbtModified}
                    body={
                      <ProfileList list={targetList} data-name="user-goal" />
                    }
                  />
                }
                locationBlock={
                  <ProfileLocation
                    userId={profile.id}
                    gender={profile.gender}
                    photo={profile.photos.default?.urls.squareSmall}
                    distance={profile.relations.distance?.text}
                    city={profile.location.city.name}
                    region={profile.location.region.name}
                  />
                }
                aboutBlock={
                  profile.dating.aboutMe && (
                    <About text={profile.dating.aboutMe.trim()} />
                  )
                }
                educationBlock={
                  profile.dating.education && (
                    <ProfileTemplate
                      title={
                        <FormattedMessage
                          id="dating_profile.education"
                          defaultMessage="Образование"
                        />
                      }
                      body={
                        <ProfileList
                          list={education}
                          fontSize={24}
                          data-name="life-style-list"
                        />
                      }
                      data-name="tags-title-section"
                    />
                  )
                }
                mobileGoalPhoto={
                  mobileGoalPhoto?.urls.squareLarge && (
                    <ProfilePhoto
                      photoId={mobileGoalPhoto.photoId}
                      source={mobileGoalPhoto.urls.squareLarge}
                      incognito={profile.incognito}
                    />
                  )
                }
                tagsBlock={
                  profile.interests.interests.nodes.length !== 0 && (
                    <ProfileTemplate
                      title={
                        <FormattedMessage
                          id="app.title.tags"
                          defaultMessage="Интересы"
                        />
                      }
                      body={
                        <Tags
                          tags={profile.interests.interests.nodes}
                          onClick={handleSaveInterestSearchClick}
                        />
                      }
                      data-name="tags-title-section"
                    />
                  )
                }
                languagesBlock={
                  languages && (
                    <ProfileTemplate
                      title={
                        <FormattedMessage
                          id="profile.language"
                          defaultMessage="Языки"
                        />
                      }
                      body={
                        <ProfileText
                          text={languages.join(', ')}
                          data-name="known-languages"
                        />
                      }
                      data-name="language-title-section"
                    />
                  )
                }
                travelBlock={
                  profile.travelAtlas.visitedCountries.visitedCountries.nodes
                    .length !== 0 && (
                    <ProfileTemplate
                      title={
                        <FormattedMessage
                          id="photo_rating.user_card.subtitle.travel"
                          defaultMessage="Путешествия"
                        />
                      }
                      body={
                        <ListCountriesFlag
                          list={
                            profile.travelAtlas.visitedCountries
                              .visitedCountries.nodes
                          }
                        />
                      }
                      data-name="travel-title-section"
                    />
                  )
                }
                mobileInterestsPhoto={
                  mobileInterestsPhoto?.urls.squareLarge && (
                    <ProfilePhoto
                      photoId={mobileInterestsPhoto.photoId}
                      source={mobileInterestsPhoto.urls.squareLarge}
                      incognito={profile.incognito}
                    />
                  )
                }
                lifestyleBlock={
                  lifestyle.length !== 0 && (
                    <ProfileTemplate
                      title={
                        <FormattedMessage
                          id="app.title.lifestyle"
                          defaultMessage="Образ жизни"
                        />
                      }
                      body={
                        <ProfileList
                          list={lifestyle}
                          fontSize={24}
                          data-name="life-style-list"
                        />
                      }
                      data-name="life-style-title-section"
                    />
                  )
                }
                mobileSocialPhoto={
                  mobileSocialPhoto?.urls.squareLarge && (
                    <ProfilePhoto
                      photoId={mobileSocialPhoto.photoId}
                      source={mobileSocialPhoto.urls.squareLarge}
                      incognito={profile.incognito}
                    />
                  )
                }
                appearanceBlock={
                  appearance.length !== 0 && (
                    <ProfileTemplate
                      title={
                        <FormattedMessage
                          id="dating_profile.appearance"
                          defaultMessage="Внешность"
                        />
                      }
                      body={<ProfileList list={appearance} fontSize={24} />}
                    />
                  )
                }
                astroBlock={
                  profile.zodiacSign && (
                    <ProfileTemplate
                      title={
                        <FormattedMessage
                          id="search.settings.sign.title"
                          defaultMessage="Знак зодиака"
                        />
                      }
                      body={
                        astro && (
                          <ProfileList
                            list={astro}
                            fontSize={24}
                            data-name="horoscope-sign"
                          />
                        )
                      }
                      data-name="horoscope-sign-title-section"
                    />
                  )
                }
                mobilePhotos={mobileLastPhotos}
                footerWave={<ProfileFooter percent={profile.compatibility} />}
              />
            </ProfileParent>
            <Scrollbar ref={scrollbarRef} />
            <ControlButtonsBar
              scrollableNode={scrollableNode}
              onHandleClick={onHandleClickControlButton}
            />
            <ShareComplaintProfile
              profileId={profile.id}
              onCallback={onCallbackComplaint}
            />
          </ClipPathInner>
        </Parent>
      </ProfileNextContext.Provider>
    )
  }

  return null
}

const Parent = styled(ClipPathOuter)`
  height: 100%;
`

const Name = styled(NameVariants)`
  color: var(--foreground-default);

  @media screen and (max-width: ${breakpoints.tablet - 1}px) {
    color: var(--static-white);
  }
`

const ProfileParent = styled.div`
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  background-color: var(--static-white);
  scrollbar-width: none;
  overflow: auto;
  overscroll-behavior-y: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (max-width: ${breakpoints.mobile}px) {
    flex-direction: column;
  }
`
